import * as React from 'react';
import moment from 'moment';
import Slider from 'react-slick';

import Text from 'components/core/Text';
import {
  ACHIEVEMENT_ICON_STATES,
  getAchievementIconComponent,
  InlineAchievementIcon,
} from '../../Achievements';

import {
  Arrows,
  Content,
  CloseIcon,
  BackgroundStyled,
  Title,
  SubTitle,
  ReachedDateTitle,
  ReachedDateValue,
  Achievement,
  AchievementBadge,
  BadgePlaceholder,
  MediaStage,
  CelebrationStage,
  Confetto,
  VideoWrapper,
  SlideVideoPlayer,
} from './styles';

const CONFETTI_PARTICLES = Array.from({ length: 18 }, (_, index) => {
  const angle = (index / 18) * Math.PI * 2 + (index % 3) * 0.23;
  const distance = 110 + (index % 5) * 34;

  return {
    tx: Math.round(Math.cos(angle) * distance),
    ty: Math.round(Math.sin(angle) * distance) - 36,
    rotation: (index % 2 ? 1 : -1) * (160 + index * 16),
    delay: 0.35 + (index % 6) * 0.04,
    colorIndex: index % 4,
  };
});

const SliderArrows = ({ className, to, onClick }) => {
  return (
    <Arrows
      theme="white"
      themeMobile="white"
      arialLabel={to}
      onClick={onClick}
      className={`button button--text button--icon ${className}`}
    />
  );
};

const SETTINGS = {
  arrows: true,
  dots: false,
  speed: 450,
  prevArrow: <SliderArrows to="prev" />,
  nextArrow: <SliderArrows to="next" />,
  infinite: false,
  swipe: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  adaptiveHeight: true,
};

const parseFocusAchievementId = (modalData) => {
  if (!modalData || typeof modalData.focusAchievementId === 'undefined') {
    return null;
  }

  const parsedValue = Number(modalData.focusAchievementId);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const buildSlidesFromProps = (props) => {
  const { notViewedAchievements, allAchievements, modalData } = props;
  const notViewedList = Array.isArray(notViewedAchievements) ? notViewedAchievements : [];
  const focusAchievementId = parseFocusAchievementId(modalData);
  const focusInNotViewed = notViewedList.some(
    (achievement) => Number(achievement.id) === focusAchievementId,
  );
  const shouldUseNotViewed = notViewedList.length > 0 && (!focusAchievementId || focusInNotViewed);
  if (shouldUseNotViewed) {
    return notViewedList;
  }

  const earnedAchievements = Array.isArray(allAchievements)
    ? allAchievements.filter((achievement) => Boolean(achievement?.earnedAt))
    : [];

  if (earnedAchievements.length > 0) {
    return earnedAchievements;
  }

  return notViewedList;
};

const getSlidesSignature = (slides) => {
  if (!Array.isArray(slides) || slides.length === 0) {
    return 'empty';
  }

  return slides
    .map((slide) => `${slide?.id ?? 'no-id'}:${slide?.earnedAt ?? slide?.updatedAt ?? ''}`)
    .join('|');
};

const computeInitialSlideIndex = (props, slidesOverride = null) => {
  const slides = Array.isArray(slidesOverride) ? slidesOverride : buildSlidesFromProps(props);

  if (!Array.isArray(slides) || slides.length === 0) {
    return 0;
  }

  const focusAchievementId = parseFocusAchievementId(props.modalData);

  if (focusAchievementId === null) {
    return 0;
  }

  const focusedIndex = slides.findIndex(
    (achievement) => Number(achievement.id) === focusAchievementId,
  );

  return focusedIndex >= 0 ? focusedIndex : 0;
};

const AchievementSlide = ({
  achievement,
  isActive,
  renderAchievement,
  achievementTitle,
  variant = 'slider',
}) => {
  const hasVideo = Boolean(achievement?.video?.muxPlaybackId);
  const [playerInstance, setPlayerInstance] = React.useState(0);
  const prevActiveRef = React.useRef(isActive);

  React.useEffect(() => {
    if (!hasVideo) {
      prevActiveRef.current = isActive;
      return;
    }

    if ((variant === 'single' || isActive) && !prevActiveRef.current) {
      setPlayerInstance((value) => value + 1);
    }

    prevActiveRef.current = isActive;
  }, [hasVideo, isActive, variant]);

  const badgeContent = React.useMemo(
    () => (achievement ? renderAchievement(achievement) : null),
    [achievement, renderAchievement],
  );

  const description = achievement?.description || '';
  const earnedDate = achievement?.earnedAt
    ? moment(achievement.earnedAt).format('dddd, MMM DD')
    : '';
  const videoPlaceholder = achievement?.video?.previewPicture || null;
  const badgeVisual = badgeContent ? <AchievementBadge>{badgeContent}</AchievementBadge> : null;
  const badgePlaceholderLabel = achievement?.name || 'Achievement';
  const badgeDisplay = badgeVisual || <BadgePlaceholder>{badgePlaceholderLabel}</BadgePlaceholder>;
  const celebrate = variant === 'single' || isActive;

  return (
    <Achievement>
      <MediaStage>
        {hasVideo ? (
          <VideoWrapper>
            <SlideVideoPlayer
              key={`achievement-video-${achievement.id}-${playerInstance}`}
              video={achievement.video}
              placeholderPicture={videoPlaceholder}
              autoPlay={false}
              muted={false}
              playsInline
              loop={false}
              controls
            />
          </VideoWrapper>
        ) : (
          <CelebrationStage
            key={`achievement-celebration-${achievement.id}-${celebrate}`}
            $celebrate={celebrate}
          >
            {celebrate &&
              Boolean(badgeVisual) &&
              CONFETTI_PARTICLES.map((particle, index) => (
                <Confetto
                  key={index}
                  $tx={particle.tx}
                  $ty={particle.ty}
                  $rotation={particle.rotation}
                  $delay={particle.delay}
                  $colorIndex={particle.colorIndex}
                />
              ))}
            {badgeDisplay}
          </CelebrationStage>
        )}
      </MediaStage>
      <Text
        variant="captionSmall"
        weight="bold"
        color="black"
        transform="uppercase"
        align="center"
        mb={7}
      >
        {achievementTitle}
      </Text>
      <ReachedDateTitle>
        <Text variant="textSmall" tag="h4" align="center" color="gray">
          {description}
        </Text>
      </ReachedDateTitle>
      <ReachedDateValue>
        <Text variant="textSmall" weight="bold" color="gray" align="center">
          {earnedDate}
        </Text>
      </ReachedDateValue>
    </Achievement>
  );
};

class Achievements extends React.Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    const slides = buildSlidesFromProps(nextProps);
    const slidesSignature = getSlidesSignature(slides);
    const focusAchievementId = parseFocusAchievementId(nextProps.modalData);
    const slidesChanged = slidesSignature !== prevState.prevSlidesSignature;
    const focusChanged = focusAchievementId !== prevState.prevFocusAchievementId;

    let nextActiveSlide = prevState.activeSlide;
    let shouldUpdate = false;

    if (slidesChanged || focusChanged) {
      nextActiveSlide = computeInitialSlideIndex(nextProps, slides);
      shouldUpdate = true;
    }

    const maxIndex = slides.length ? slides.length - 1 : 0;
    if (nextActiveSlide > maxIndex) {
      nextActiveSlide = Math.max(0, maxIndex);
      shouldUpdate = true;
    }

    if (!slides.length && nextActiveSlide !== 0) {
      nextActiveSlide = 0;
      shouldUpdate = true;
    }

    if (shouldUpdate || slidesChanged || focusChanged) {
      return {
        activeSlide: nextActiveSlide,
        prevSlidesSignature: slidesSignature,
        prevFocusAchievementId: focusAchievementId,
      };
    }

    return null;
  }

  constructor(props) {
    super(props);
    const initialSlides = buildSlidesFromProps(props);
    this.state = {
      activeSlide: computeInitialSlideIndex(props, initialSlides),
      prevSlidesSignature: getSlidesSignature(initialSlides),
      prevFocusAchievementId: parseFocusAchievementId(props.modalData),
    };
    this.sliderRef = null;
    this.lastChangeOriginatedFromSlider = false;
  }

  setSliderRef = (ref) => {
    this.sliderRef = ref;
  };

  handleAfterChange = (nextSlide) => {
    if (this.state.activeSlide !== nextSlide) {
      this.lastChangeOriginatedFromSlider = true;
      this.setState({ activeSlide: nextSlide });
    }
  };

  getSlides(currentProps = this.props) {
    return buildSlidesFromProps(currentProps);
  }

  isSingleView(slides) {
    const { modalData } = this.props;
    if (modalData?.showSingleAchievement) {
      return true;
    }

    return !slides || slides.length <= 1;
  }

  getInitialSlideIndex(currentProps = this.props, slidesOverride = null) {
    return computeInitialSlideIndex(currentProps, slidesOverride);
  }

  handleCloseAchievements = () => {
    const { requestFetchAchievements } = this.props;

    requestFetchAchievements({ markAsViewed: true });
  };

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.activeSlide !== this.state.activeSlide) {
      if (this.lastChangeOriginatedFromSlider) {
        this.lastChangeOriginatedFromSlider = false;
      } else if (this.sliderRef && typeof this.sliderRef.slickGoTo === 'function') {
        this.sliderRef.slickGoTo(this.state.activeSlide);
      }
    }
  }

  renderAchievement = (achievement) => {
    if (!achievement) {
      return null;
    }

    const AchievementComponent = getAchievementIconComponent(
      achievement,
      ACHIEVEMENT_ICON_STATES.earned,
    );

    if (AchievementComponent) {
      return <AchievementComponent />;
    }

    if (achievement.icon) {
      return <InlineAchievementIcon className="achievement-icon-inline" html={achievement.icon} />;
    }

    return null;
  };

  renderAchievements = (slidesParam) => {
    const slides = slidesParam || this.getSlides();
    const { activeSlide } = this.state;

    if (!slides || !slides.length) {
      return null;
    }

    return slides.map((achievement, index) => (
      <AchievementSlide
        key={achievement.id}
        achievement={achievement}
        isActive={activeSlide === index}
        renderAchievement={this.renderAchievement}
        achievementTitle={achievement.name}
      />
    ));
  };

  renderSingleAchievement(slide) {
    if (!slide) {
      return null;
    }

    return (
      <AchievementSlide
        achievement={slide}
        isActive
        variant="single"
        renderAchievement={this.renderAchievement}
        achievementTitle={slide.name}
      />
    );
  }

  render() {
    const slides = this.getSlides();
    const singleView = this.isSingleView(slides);
    const { activeSlide } = this.state;
    const hasSlides = Array.isArray(slides) && slides.length > 0;
    const initialSlideIndex = this.getInitialSlideIndex(this.props, slides);
    let modalBody = null;

    if (singleView) {
      const focusedSlide = hasSlides ? slides[initialSlideIndex] : null;
      modalBody = this.renderSingleAchievement(focusedSlide);
    } else if (hasSlides) {
      modalBody = (
        <Slider
          ref={this.setSliderRef}
          arrows={SETTINGS.arrows}
          dots={SETTINGS.dots}
          speed={SETTINGS.speed}
          prevArrow={SETTINGS.prevArrow}
          nextArrow={SETTINGS.nextArrow}
          infinite={SETTINGS.infinite}
          swipe={SETTINGS.swipe}
          slidesToShow={SETTINGS.slidesToShow}
          slidesToScroll={SETTINGS.slidesToScroll}
          adaptiveHeight={SETTINGS.adaptiveHeight}
          initialSlide={activeSlide}
          afterChange={this.handleAfterChange}
        >
          {this.renderAchievements(slides)}
        </Slider>
      );
    }

    return (
      <>
        <BackgroundStyled onClick={this.handleCloseAchievements} />
        <Content>
          <CloseIcon onClick={this.handleCloseAchievements} />
          <Title>
            <Text variant="promoHeader" color="grayExtraLight" align="center" isCondensed>
              achievement
            </Text>
            <SubTitle>
              <Text variant="displayX" tag="div" color="black" align="center" transform="uppercase">
                reached
              </Text>
            </SubTitle>
          </Title>
          {modalBody}
        </Content>
      </>
    );
  }
}

export default Achievements;
